import {
  type ConsentList,
  ConsentListSchema,
  ConsentResponseSchema,
  type CreatePrivacyRequest,
  currentConsentVersion,
  DataExportLinkSchema,
  DataExportResponseSchema,
  DataSummarySchema,
  DeletedChatsSchema,
  DeletionPreviewSchema,
  MeResponseSchema,
  MetaResponseSchema,
  PrivacyRequestListSchema,
  PrivacyRequestResponseSchema,
} from '@oneapp/shared';
import { z } from 'zod';
import { api } from '../lib/api';

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

/**
 * Privacy and data rights (Phase 17, see packages/shared/src/privacy.ts): what EkApp holds, downloading
 * it, deleting the account or the chats, withdrawing what was allowed, and asking about your data.
 */
export const privacyApi = {
  summary: (signal?: AbortSignal) =>
    api.get('/api/v1/me/data-summary', DataSummarySchema, { auth: true, ...withSignal(signal) }),

  consents: (signal?: AbortSignal) =>
    api.get('/api/v1/me/consents', ConsentListSchema, { auth: true, ...withSignal(signal) }),

  /** Agree to the current version of a consent text. */
  agree: (type: 'privacy_notice' | 'adult' | 'ai_photos') =>
    api.post('/api/v1/me/consents', ConsentResponseSchema, {
      auth: true,
      body: { type, version: currentConsentVersion(type) },
    }),

  /** `ai_photos`, or a data consent's id (GST data disconnects; a statement's payments are erased). */
  withdraw: (id: string) =>
    api.delete(`/api/v1/me/consents/${encodeURIComponent(id)}`, ConsentListSchema, { auth: true }),

  // Download my data (D3, D4).
  latestExport: (signal?: AbortSignal) =>
    api.get('/api/v1/me/exports/latest', DataExportResponseSchema, { auth: true, ...withSignal(signal) }),
  requestExport: () => api.post('/api/v1/me/exports', DataExportResponseSchema, { auth: true }),
  exportLink: (id: string) =>
    api.get(`/api/v1/me/exports/${encodeURIComponent(id)}/link`, DataExportLinkSchema, { auth: true }),
  removeExport: (id: string) => api.delete(`/api/v1/me/exports/${encodeURIComponent(id)}`, z.null(), { auth: true }),

  // Deleting the account (D5, D7) and the chats (D8).
  deletion: (signal?: AbortSignal) =>
    api.get('/api/v1/me/deletion', DeletionPreviewSchema, { auth: true, ...withSignal(signal) }),
  deleteAccount: (confirm: string) =>
    api.delete('/api/v1/me', DeletionPreviewSchema, { auth: true, body: { confirm } }),
  keepAccount: () => api.post('/api/v1/me/deletion/cancel', MeResponseSchema, { auth: true }),
  deleteChats: () => api.delete('/api/v1/me/conversations', DeletedChatsSchema, { auth: true }),

  // Asking about your data, or complaining (D10).
  requests: (signal?: AbortSignal) =>
    api.get('/api/v1/me/privacy-requests', PrivacyRequestListSchema, { auth: true, ...withSignal(signal) }),
  ask: (body: CreatePrivacyRequest) =>
    api.post('/api/v1/me/privacy-requests', PrivacyRequestResponseSchema, { auth: true, body }),

  /** Who runs EkApp and its Grievance Officer: public, the privacy notice shows them before sign-up. */
  meta: (signal?: AbortSignal) => api.get('/api/v1/meta', MetaResponseSchema, withSignal(signal)),
};

export const privacyKeys = {
  consents: ['me', 'consents'] as const,
  summary: ['me', 'data-summary'] as const,
  latestExport: ['me', 'exports', 'latest'] as const,
  deletion: ['me', 'deletion'] as const,
  requests: ['me', 'privacy-requests'] as const,
  meta: ['meta'] as const,
};

/** Invoice photos may be read by AI: allowed (the current text), and not turned off since (D1, D11). */
export function aiPhotosAllowed(list: ConsentList | undefined): boolean {
  return Boolean(
    list?.consents.some(
      (c) => c.type === 'ai_photos' && c.version === currentConsentVersion('ai_photos') && !c.withdrawnAt,
    ),
  );
}
